import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Loader2 } from "lucide-react";

interface Hospital {
  id: string;
  name: string;
  npi_number?: string;
  address: string;
  city: string;
  state: string;
  zip_code?: string;
  phone_number?: string;
  fax_number?: string;
  latitude: number;
  longitude: number;
  shame_score: number;
  avg_markup_percentage: number;
  total_procedures: number;
  followed: boolean;
  data_obfuscation_score?: number;
  avg_file_size_mb?: number;
  file_complexity_score?: number;
  total_chargemaster_files?: number;
  website_url?: string;
  transparency_portal_url?: string;
  shoppable_services_url?: string;
  chargemaster_url?: string;
  [key: string]: any;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

interface HospitalEditModalProps {
  hospital: Hospital | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (hospital: Hospital) => void;
}

export const HospitalEditModal = ({ hospital, isOpen, onClose, onSave }: HospitalEditModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingHospital, setEditingHospital] = useState<Hospital | null>(hospital);
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Update local state when hospital prop changes
  useState(() => {
    if (hospital) {
      setEditingHospital(hospital);
    }
  });

  // Update hospital mutation
  const updateHospitalMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Hospital> }) => {
      const { error } = await supabase
        .from('hospitals')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
      return { ...hospital, ...updates };
    },
    onSuccess: (updatedHospital) => {
      toast({
        title: "Hospital Updated",
        description: "Hospital has been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      onSave?.(updatedHospital);
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const handleGeocodeHospital = async () => {
    if (!editingHospital) return;
    
    setIsGeocoding(true);
    
    try {
      // Build address string
      const addressParts = [
        editingHospital.address,
        editingHospital.city,
        editingHospital.state,
        editingHospital.zip_code
      ].filter(Boolean);
      
      const fullAddress = addressParts.join(', ');
      
      if (!fullAddress.trim()) {
        toast({
          title: "Error",
          description: "Please fill in address, city, and state before geocoding",
          variant: "destructive",
        });
        return;
      }

      // Get Google API key
      const { data: keyData, error: keyError } = await supabase.functions.invoke('get-google-api-key');
      
      if (keyError || !keyData?.apiKey) {
        throw new Error('Google API key not available');
      }

      // Call Google Geocoding API
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fullAddress)}&key=${keyData.apiKey}`;
      
      const response = await fetch(geocodeUrl);
      const data = await response.json();
      
      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        
        setEditingHospital({
          ...editingHospital,
          latitude: location.lat,
          longitude: location.lng
        });
        
        toast({
          title: "Success",
          description: `Geocoded location: ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`,
        });
      } else {
        let errorMsg = 'Geocoding failed';
        
        switch (data.status) {
          case 'ZERO_RESULTS':
            errorMsg = 'No results found for this address';
            break;
          case 'REQUEST_DENIED':
            errorMsg = 'Geocoding request denied - check API key';
            break;
          case 'OVER_QUERY_LIMIT':
            errorMsg = 'API quota exceeded';
            break;
          default:
            errorMsg = `Geocoding failed: ${data.status}`;
        }
        
        toast({
          title: "Geocoding Failed",
          description: errorMsg,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Geocoding Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleSave = () => {
    if (!editingHospital) return;
    
    updateHospitalMutation.mutate({
      id: editingHospital.id,
      updates: editingHospital
    });
  };

  if (!editingHospital) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Hospital</DialogTitle>
          <DialogDescription>
            Update hospital information and settings.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 space-y-4 md:space-y-0">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Hospital Name *</Label>
              <Input
                id="name"
                value={editingHospital.name}
                onChange={(e) => setEditingHospital({ ...editingHospital, name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="npi_number">NPI Number</Label>
              <Input
                id="npi_number"
                value={editingHospital.npi_number || ''}
                onChange={(e) => setEditingHospital({ ...editingHospital, npi_number: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={editingHospital.address}
                onChange={(e) => setEditingHospital({ ...editingHospital, address: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={editingHospital.city}
                  onChange={(e) => setEditingHospital({ ...editingHospital, city: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="state">State *</Label>
                <Select value={editingHospital.state} onValueChange={(value) => setEditingHospital({ ...editingHospital, state: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  value={editingHospital.zip_code || ''}
                  onChange={(e) => setEditingHospital({ ...editingHospital, zip_code: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone_number">Phone</Label>
                <Input
                  id="phone_number"
                  value={editingHospital.phone_number || ''}
                  onChange={(e) => setEditingHospital({ ...editingHospital, phone_number: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Location and Settings */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="latitude">Latitude *</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  value={editingHospital.latitude}
                  onChange={(e) => setEditingHospital({ ...editingHospital, latitude: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="longitude">Longitude *</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  value={editingHospital.longitude}
                  onChange={(e) => setEditingHospital({ ...editingHospital, longitude: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>

            <Button
              onClick={handleGeocodeHospital}
              disabled={isGeocoding}
              variant="outline"
              className="w-full"
            >
              {isGeocoding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <MapPin className="w-4 h-4 mr-2" />
              )}
              {isGeocoding ? 'Geocoding...' : 'Geocode Address'}
            </Button>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="shame_score">Shame Score</Label>
                <Input
                  id="shame_score"
                  type="number"
                  min="0"
                  max="100"
                  value={editingHospital.shame_score}
                  onChange={(e) => setEditingHospital({ ...editingHospital, shame_score: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="avg_markup_percentage">Avg Markup %</Label>
                <Input
                  id="avg_markup_percentage"
                  type="number"
                  step="0.01"
                  value={editingHospital.avg_markup_percentage}
                  onChange={(e) => setEditingHospital({ ...editingHospital, avg_markup_percentage: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="followed"
                checked={editingHospital.followed}
                onCheckedChange={(checked) => setEditingHospital({ ...editingHospital, followed: checked })}
              />
              <Label htmlFor="followed">Following for indexing</Label>
            </div>

            <div>
              <Label htmlFor="website_url">Website URL</Label>
              <Input
                id="website_url"
                value={editingHospital.website_url || ''}
                onChange={(e) => setEditingHospital({ ...editingHospital, website_url: e.target.value })}
                className="mt-1"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <Label htmlFor="transparency_portal_url">Transparency Portal URL</Label>
              <Input
                id="transparency_portal_url"
                value={editingHospital.transparency_portal_url || ''}
                onChange={(e) => setEditingHospital({ ...editingHospital, transparency_portal_url: e.target.value })}
                className="mt-1"
                placeholder="https://example.com/transparency"
              />
            </div>

            <div>
              <Label htmlFor="chargemaster_url">Chargemaster URL</Label>
              <Input
                id="chargemaster_url"
                value={editingHospital.chargemaster_url || ''}
                onChange={(e) => setEditingHospital({ ...editingHospital, chargemaster_url: e.target.value })}
                className="mt-1"
                placeholder="https://example.com/chargemaster"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={updateHospitalMutation.isPending}
          >
            {updateHospitalMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};